'use client';

import { cn, formatNumber } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import React, { useEffect, useRef, useState } from 'react';

/**
 * Component for animating numbers.
 *
 * @param {number} n - The target number to animate to.
 * @param {boolean} isVisible - Whether the number is currently visible (used to trigger the animation).
 */
interface AnimatedNumberArgs {
    n: number;
    isVisible: boolean;
    className?: string;
    onAnimationEnd?: () => void;
    isPercent?: boolean;
}

export function AnimatedNumber({
    n,
    isVisible,
    className,
    onAnimationEnd,
    isPercent,
}: AnimatedNumberArgs) {
    const [displayNumber, setDisplayNumber] = useState(0);

    useEffect(() => {
        if (isVisible) {
            let start = 0;
            const end = n;
            const duration = 1500;
            const increment = (end / duration) * 16.67; // Assuming 60 frames per second

            const animate = () => {
                start += increment;
                if (start < end) {
                    setDisplayNumber(Math.ceil(start));
                    requestAnimationFrame(animate);
                } else {
                    setDisplayNumber(end);
                    if (onAnimationEnd) onAnimationEnd();
                }
            };

            animate();
        } else {
            setDisplayNumber(0);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, n]);

    return (
        <div className={className}>
            {formatNumber(displayNumber)}
            {isPercent && '%'}
        </div>
    );
}

/**
 * Component for displaying a circular section with a title and label.
 *
 * @param {string} bgColor - Background color of the circle.
 * @param {string} borderColor - Border color of the circle.
 * @param {React.ReactNode} [value] - The value to display inside the circle (usually a number).
 * @param {string} [title] - The title to display inside the circle.
 * @param {string} [label] - The label to display below the title.
 * @param {string} [titleStyle] - Additional styles for the title.
 */

/**
 * Main component for the informational section.
 *
 * @param {numbe} number - A number to display and animate.
 */
interface InformationalSectionProps {
    number: number;
    className?: string;
    maxWidth?: number;
    isPercent?: boolean;
    dark?: boolean;
}

const AnimatedNumberSection: React.FC<InformationalSectionProps> = ({
    number,
    className,
    maxWidth = 252,
    isPercent = false,
    dark = false,
}) => {
    const t = useTranslations('HomePage');
    const [isAnimatedNumberVisible, setAnimatedNumberVisible] = useState(false);
    const [isAnimationCompleted, setAnimationCompleted] = useState(false);
    const animatedNumberRef = useRef<HTMLDivElement>(null);

    // Intersection observer to detect visibility of numbers
    useEffect(() => {
        const options = {
            root: null,
            rootMargin: '0px',
            threshold: 0.5,
        };

        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setAnimatedNumberVisible(true);
                }
            });
        };

        const observer = new IntersectionObserver(handleIntersection, options);

        if (animatedNumberRef.current) {
            observer.observe(animatedNumberRef.current);
        }

        return () => {
            if (animatedNumberRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                observer.unobserve(animatedNumberRef.current);
            }
        };
    }, []);

    return (
        <div className="relative w-fit" ref={animatedNumberRef} style={{ maxWidth }}>
            {!isPercent && (
                <span
                    className={cn(
                        'text-white opacity-0 absolute top-0 right-0 text-sm transition-all duration-500',
                        dark && "text-dark-grey",
                        isAnimationCompleted && 'opacity-60',
                    )}
                >
                    {t('manufacturedParts')}
                </span>
            )}
            <AnimatedNumber
                className={cn('text-white opacity-60 leading-tight', dark && "text-dark-grey", className)}
                n={number}
                isVisible={isAnimatedNumberVisible}
                onAnimationEnd={() => setAnimationCompleted(true)}
                isPercent={isPercent}
            />
        </div>
    );
};

export default AnimatedNumberSection;
